package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.RevenueSource
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.salesimport.SalesDay
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import kotlinx.datetime.number
import kotlin.math.abs
import kotlin.time.Clock

/** SH-123: what an import does with one day of the file. */
enum class SalesImportAction {
    /** The day has no takings yet: the file's figure goes in. */
    IMPORT,
    /** The day's takings came from an earlier import (CSV or till): the file's figure replaces them. */
    REPLACE_IMPORT,
    /** The same figure is already there. */
    UNCHANGED,
    /** Takings typed by hand win: the day is left as it is. */
    KEEP_MANUAL,
    /** The day is closed (reconciled): left as it is. */
    DAY_CLOSED;

    val writes: Boolean get() = this == IMPORT || this == REPLACE_IMPORT
}

/** One day of the preview: the file's figures, what is stored now and what the import will do. */
data class SalesImportLine(
    val day: SalesDay,
    val action: SalesImportAction,
    val currentNetto: Double? = null,
    val currentSource: RevenueSource? = null
)

data class SalesImportPlan(val workplaceId: String, val lines: List<SalesImportLine>) {
    val toWrite: Int get() = lines.count { it.action.writes }
    fun count(action: SalesImportAction): Int = lines.count { it.action == action }
}

/** What an applied import did. */
data class SalesImportResult(val written: Int, val keptManual: Int, val closed: Int, val unchanged: Int)

/** Only owners and financial managers import takings (the rules' isFinancialManager). */
class SalesImportNotAllowedException : Exception("permission-denied: importing takings needs VIEW_FINANCES")

/**
 * SH-123: brings a sales file's days ([SalesDay], read by
 * [com.evoapps.evoshift.domain.salesimport.SalesSheetParser]) into the daily records: the net
 * figure becomes the day's takings (`revenue.realNetto`, gross into `realBrutto` when given),
 * marked `source = CSV` with `importedAt`. Takings typed by hand are never replaced (also those
 * stored before SH-123, which carry no source); closed days stay closed. Each day is recomputed
 * like a typed figure (labour % of the takings, staff without clock-ins counted as planned), one
 * month at a time with one read of its data and one batch write.
 *
 * [plan] is the preview; [apply] plans again against the data as it is then (someone may have
 * typed a figure meanwhile) and writes.
 */
class ImportDailySalesUseCase(
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase,
    private val caller: CallerCapabilities? = null,
    private val clock: Clock = AppClock
) {
    suspend fun plan(workplaceId: String, days: List<SalesDay>): Result<SalesImportPlan> = runCatching {
        if (!caller.allows(workplaceId, Capability.VIEW_FINANCES)) throw SalesImportNotAllowedException()
        val lines = days.groupBy { rosterIdOf(workplaceId, it) }.flatMap { (rosterId, monthDays) ->
            val stored = financialRepository.getDailyRecordsForRoster(rosterId).getOrThrow().associateBy { it.date }
            monthDays.map { day -> lineFor(day, stored[day.date.toString()]) }
        }
        SalesImportPlan(workplaceId, lines.sortedBy { it.day.date })
    }

    suspend fun apply(workplaceId: String, days: List<SalesDay>): Result<SalesImportResult> = runCatching {
        if (!caller.allows(workplaceId, Capability.VIEW_FINANCES)) throw SalesImportNotAllowedException()
        val importedAt = clock.now().toString()
        var written = 0
        val lines = mutableListOf<SalesImportLine>()
        days.groupBy { rosterIdOf(workplaceId, it) }.forEach { (rosterId, monthDays) ->
            val stored = financialRepository.getDailyRecordsForRoster(rosterId).getOrThrow().associateBy { it.date }
            val monthLines = monthDays.map { day -> lineFor(day, stored[day.date.toString()]) }
            lines += monthLines
            val toWrite = monthLines.filter { it.action.writes }
            if (toWrite.isEmpty()) return@forEach
            val inputs = calculateDailyRecordUseCase.load(workplaceId, rosterId).getOrThrow()
            val records = toWrite.map { line ->
                val date = line.day.date.toString()
                val kept = DailyRevenueInput.keptFrom(stored[date])
                calculateDailyRecordUseCase.compute(
                    workplaceId, rosterId, date, inputs,
                    kept.copy(
                        actualNetto = line.day.net,
                        actualBrutto = line.day.gross ?: kept.actualBrutto,
                        source = RevenueSource.CSV,
                        importedAt = importedAt
                    )
                ).copy(isLocked = stored[date]?.isLocked ?: false)
            }
            financialRepository.saveDailyRecords(records).getOrThrow()
            written += records.size
        }
        SalesImportResult(
            written = written,
            keptManual = lines.count { it.action == SalesImportAction.KEEP_MANUAL },
            closed = lines.count { it.action == SalesImportAction.DAY_CLOSED },
            unchanged = lines.count { it.action == SalesImportAction.UNCHANGED }
        )
    }

    private fun rosterIdOf(workplaceId: String, day: SalesDay): String =
        RosterIds.of(workplaceId, day.date.year, day.date.month.number)

    private fun lineFor(day: SalesDay, stored: DailyRecord?): SalesImportLine {
        val revenue = stored?.revenue
        val action = when {
            stored?.isLocked == true -> SalesImportAction.DAY_CLOSED
            revenue?.isEnteredByHand == true -> SalesImportAction.KEEP_MANUAL
            revenue?.realNetto == null -> SalesImportAction.IMPORT
            sameAmount(revenue.realNetto, day.net) && (day.gross == null || sameAmount(revenue.realBrutto, day.gross)) ->
                SalesImportAction.UNCHANGED
            else -> SalesImportAction.REPLACE_IMPORT
        }
        return SalesImportLine(day, action, revenue?.realNetto, revenue?.source)
    }

    private fun sameAmount(a: Double?, b: Double?): Boolean = a != null && b != null && abs(a - b) < 0.005
}
